/**
 *  Model related logic, including the getting of a model out of a collection, adding
 *  it to a collection (once) and casting all it's date properties.
 */
import * as Cast from './cast';
import { forOwn, forEach } from './../../utils';
import { ShopError } from '../../internals/errors';
import { slugError } from '../../internals/errors/errors';
/**
 *  Consts which can be provided as fourth param to the getFrom method
 *  for getting the value of a model or a ref to the model.
 */
export const GET_BY_VALUE = true;
export const GET_BY_REF = false;
/**
 *  Const which can be provided as fifth param to the getFrom method
 *  for not throwing an error when a model could not be found.
 */
export const IGNORE_UNDEFINED = true;
export default {
    /**
     *
     *  Used to get the reference or value of a model out of the provided data object.
     *
     *  @param data Object that holds all the data out of which the model should be get
     *  @param key The key of the collection within the data out of which the model should be get
     *  @param guid The guid of the model to get
     *  @param value If the value of the model should be get, instead of a reference
     *  @param ignoreUndefined If the method should throw an error if the model could not be found
     *
     */
    getFrom(data, key, guid, value = false, ignoreUndefined = false) {
        if (!data[key]) {
            throw slugError(`The provided data does not contain the key ${key}`, ['model', 'getFrom', 'error', 'invalid'], { key });
        }
        let _models = [];
        let _model;
        // Create a local Array with model references, as the data to get
        // the model out of can be provided as an Array or Object
        if (Array.isArray(data[key])) {
            _models = data[key];
        }
        else {
            forOwn(data[key], (model) => {
                _models.push(model);
            });
        }
        forEach(_models, (model) => {
            if (model.guid === guid) {
                _model = model;
            }
            return !_model;
        });
        if (!_model) {
            // Do not throw an error when undefined models should be ignored
            if (ignoreUndefined) {
                return undefined;
            }
            throw slugError(`No model in ${key} with guid: ${guid}`, ['model', 'getFrom', 'error', 'notfound'], { key, guid });
        }
        // Return the value if requested
        // noinspection PointlessBooleanExpressionJS
        return value === true ? JSON.parse(JSON.stringify(_model)) : _model;
    },
    /**
     *
     *  Used to add a model reference to a collection (once); because it may happen that a specific
     *  model is returned out of the API more than once.
     *
     *  @param collection An Array of the models or a guid map Object of the models
     *  @param model Any type of model, **must include a guid to check his uniqueness**
     *
     */
    addTo(collection, model) {
        if (!model.guid) {
            throw new ShopError(`The model to add to a collection must have a guid: ${model}`);
        }
        if (Array.isArray(collection)) {
            if (!collection.length) {
                collection.push(model);
                return;
            }
            let alreadyAdded = false;
            forEach(collection, (collectionModel) => {
                if (collectionModel.guid === model.guid) {
                    alreadyAdded = true;
                    return false;
                }
            });
            if (!alreadyAdded) {
                collection.push(model);
            }
            return;
        }
        if (typeof collection === 'object') {
            // Currently Object.assign is used to make sure only top level values are
            // overwritten and underlaying refs are broken, because it's behaviour we
            // want at this moment: every linked model in a model is added to the
            // updated model again after it's updated in the collection
            collection[model.guid] = collection[model.guid]
                ? Object.assign(collection[model.guid], model)
                : model;
            return;
        }
        throw new ShopError(`The collection to add a model to should be an Array or Object: ${collection}`);
    },
    /**
     *  Simplied interface for casting the needed data in a
     *  single or collection of a specific model.
     */
    castData: {
        ofEvent: Cast.event,
        ofEvents: Cast.events,
        ofEventDate: Cast.eventDate,
        ofEventDates: Cast.eventDates,
        ofTicket: Cast.ticket,
        ofTickets: Cast.tickets,
    },
};
//# sourceMappingURL=model.js.map